const _ = require('lodash');
const { assert } = require('../common/assert');
const { makePointCloud } = require('../point-cloud');
const { makeSurface } = require('../surface');

// a container defined by a series of 2D shapes that lie on planes parallel with the x-y plane (in other words,
// the z coordinate is constant for all points in the shape)
class PrismoidSeriesContainer {
  #shapes;

  #surface;

  constructor(shapes) {
    // sorts shapes top to bottom
    this.#shapes = _.sortBy(shapes, 'z', 'desc');
    assert(this.#isUnwaisted(), 'Container must be unwaisted!');
  }

  boundingBox() {
    const boundingRects = this.#shapes.map(shape => shape.boundingRect());
    const minX = _.min(boundingRects.map(({ minX }) => minX));
    const minY = _.min(boundingRects.map(({ minY }) => minY));
    const minZ = _.min(this.#shapes.map(({ z }) => z));
    const maxX = _.max(boundingRects.map(({ maxX }) => maxX));
    const maxY = _.max(boundingRects.map(({ maxY }) => maxY));
    const maxZ = _.max(this.#shapes.map(({ z }) => z));
    return { minX, minY, minZ, maxX, maxY, maxZ };
  }

  toSurface() {
    if (!this.#surface) {
      this.#surface = this.createSurface();
    }
    return this.#surface;
  }

  createSurface() {
    const shapePoints = this.#shapes.map(shape => this.shapeToSurfacePoints(shape));
    const pointsPerShape = shapePoints[0].length;
    assert(shapePoints.every(points => points.length === pointsPerShape), 'All shapes must have the same number of points');

    const triangles = shapePoints.flatMap((points, shapeIdx) => {
      if (shapeIdx === this.#shapes.length - 1) {
        return [];
      }
      return points.flatMap((_, pointIdx) => {
        const a = pointIdx;
        const b = (pointIdx + 1) % pointsPerShape;
        const s0a = shapeIdx * pointsPerShape + a;
        const s0b = shapeIdx * pointsPerShape + b;
        const s1a = (shapeIdx + 1) * pointsPerShape + a;
        const s1b = (shapeIdx + 1) * pointsPerShape + b;

        return [
          { i: s0a, j: s1a, k: s1b },
          { i: s0a, j: s1b, k: s0b },
        ];
      });
    });
    const allPoints = this.#shapes.flatMap(shape => this.shapeToSurfacePoints(shape));

    return makeSurface(
      makePointCloud(
        allPoints.map(({ x }) => x),
        allPoints.map(({ y }) => y),
        allPoints.map(({ z }) => z),
      ),
      triangles.map(({ i }) => i),
      triangles.map(({ j }) => j),
      triangles.map(({ k }) => k),
    );
  }

  shapeToSurfacePoints() {
    throw new Error('Not implemented: shapeToSurfacePoints');
  }

  zIntercepts(x, y) {
    const firstShapeIndexContainingPoint = _.findIndex(this.#shapes, shape => shape.containsPoint(x, y));
    const lastShapeIndexContainingPoint = _.findLastIndex(this.#shapes, shape => shape.containsPoint(x, y));
    let min;
    if (firstShapeIndexContainingPoint > 0) {
      min = this.interpolateZAtPoint(this.#shapes[firstShapeIndexContainingPoint - 1], this.#shapes[firstShapeIndexContainingPoint], x, y);
    } else if (firstShapeIndexContainingPoint === 0) {
      min = this.#shapes[firstShapeIndexContainingPoint].z;
    }

    let max;
    if (lastShapeIndexContainingPoint >= 0 && lastShapeIndexContainingPoint < this.#shapes.length - 1) {
      max = this.interpolateZAtPoint(this.#shapes[lastShapeIndexContainingPoint + 1], this.#shapes[lastShapeIndexContainingPoint], x, y);
    } else if (lastShapeIndexContainingPoint === this.#shapes.length - 1) {
      max = this.#shapes[lastShapeIndexContainingPoint].z;
    }

    return [min, max];
  }

  interpolateZAtPoint(/* shape1, shape2, x, y */) {
    throw new Error('Not implemented: interpolateZAtPoint');
  }

  // This is an imperfect term I made up because I don't know the real term for it. The visual analogy is that if you
  // had a level (consistent z) belt around any part of the container, you could get it off by moving it straight up or
  // down (+/- z translation), no lateral twisting or movement required.
  //
  // What it means for us is that if you take any line parallel to the z-axis, it will interesect the container at most
  // twice. Meaning also that there is a clear minimum and maximum z value for any x-y point in the container.
  //
  // Note that all convex solids will be unwaisted, but not all unwaisted solids are convex. A bin with a non-zero
  // lip height is an example of a non-convex unwaisted solid.
  #isUnwaisted() {
    let gettingBigger = true;
    for (let i = 0; i < this.#shapes.length - 1; i++) {
      const first = this.#shapes[i];
      const second = this.#shapes[i + 1];
      if (gettingBigger) {
        if (second.fullyContains(first)) {
          continue;
        }
        if (first.fullyContains(second)) {
          gettingBigger = false;
          continue;
        }
        return false;
      }
      // once we're getting smaller we can't get bigger again
      if (first.fullyContains(second)) {
        continue;
      }
      return false;
    }
    return true;
  }
}

exports.PrismoidSeriesContainer = PrismoidSeriesContainer;
